/**
 * RadiologyCopilotWidget — AI-powered conversational assistant for radiologists.
 *
 * Appears as a collapsible panel that can be embedded on:
 *   - Patient history page
 *   - Dictation history page
 *   - Report editor
 *   - Analytics dashboard
 *
 * Supports natural language queries with follow-up conversation context.
 * Three window modes: float (default) → user-drag-resize → full-screen maximize.
 * Maximized mode has left (session outline) + right (sources) sidebars.
 * Per-message copy and whole-session PDF export included.
 */

// html2pdf.js ships without bundled TypeScript types.
// We cast the dynamic import to a minimal typed interface below.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Fab,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SourceIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MicIcon from '@mui/icons-material/Mic';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import aiCopilotService, {
  AIQueryContext,
  AIQueryResponse,
  SourceReference,
} from '../services/aiCopilotService';
import { typography } from '~/theme';

// ── Constants ────────────────────────────────────────────────────────────

const DEFAULT_W = 440;
const DEFAULT_H = 580;
const MIN_W = 320;
const MIN_H = 400;
const MAX_SOURCES_COLLAPSED = 6;

// ── Types ────────────────────────────────────────────────────────────────

interface TimelineEntry {
  date: string | null;
  study_type: string;
  modality: string;
  findings: string;
  status: string;
}

interface ProgressionEvidence {
  type: string;
  label: string;
  status: string;
  current_value?: number;
  prior_value?: number;
  change_pct?: number;
  current_grade?: string;
  prior_grade?: string;
  unit?: string;
  details?: Record<string, unknown>;
}

interface ProgressionEntry {
  score: number;
  direction: string;
  summary: string;
  evidence: ProgressionEvidence[];
  recommendations: string[];
  current_study?: {
    date?: string;
    modality?: string;
    anatomy_region?: string;
    summary?: string;
  };
  prior_study?: {
    date?: string;
    modality?: string;
    anatomy_region?: string;
    summary?: string;
  };
}

interface ComparisonEntry {
  has_prior: boolean;
  agreement_score: number;
  current_study?: {
    date?: string;
    modality?: string;
    anatomy_region?: string;
    summary?: string;
  };
  prior_study?: {
    date?: string;
    modality?: string;
    anatomy_region?: string;
    summary?: string;
  };
  new_findings: string[];
  resolved_findings: string[];
  stable_findings: string[];
  measurement_changes: {
    label: string;
    current_value: number;
    prior_value: number;
    change_pct: number;
    unit: string;
  }[];
  summary_text: string;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceReference[];
  confidence?: number;
  intent?: string;
  followUpSuggestions?: string[];
  metadata?: Record<string, unknown>;
  timestamp: Date;
}

interface RadiologyCopilotWidgetProps {
  /** Optional patient context for scoped queries */
  patientId?: string;
  /** Optional study context */
  studyId?: number;
  /** Optional session context */
  sessionId?: string;
  /** Position variant: 'fab' (floating button) or 'inline' (embedded panel) */
  variant?: 'fab' | 'inline';
  /** Default open state */
  defaultOpen?: boolean;
}

// ── Source type chip colours ──────────────────────────────────────────────

function sourceTypeColor(type: string): 'primary' | 'secondary' | 'success' | 'default' {
  if (type === 'report') return 'primary';
  if (type === 'transcription') return 'secondary';
  if (type === 'study') return 'success';
  return 'default';
}

// ── Confidence indicator ─────────────────────────────────────────────────

const ConfidenceIndicator: React.FC<{ confidence: number }> = ({ confidence }) => {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';
  return (
    <Tooltip title={`Confidence: ${pct}%`}>
      <Chip
        size="small"
        label={`${pct}%`}
        color={color}
        variant="outlined"
        sx={{ fontSize: typography.caption.fontSize, height: 18, ml: 0.75 }}
      />
    </Tooltip>
  );
};

// ── Source references display ─────────────────────────────────────────────

const SourcesList: React.FC<{ sources: SourceReference[] }> = ({ sources }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (!sources || sources.length === 0) return null;

  const visible = showAll ? sources : sources.slice(0, MAX_SOURCES_COLLAPSED);

  return (
    <Box sx={{ mt: 1.25 }}>
      {/* Toggle pill */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 99,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          bgcolor: 'grey.100',
          '&:hover': { bgcolor: 'grey.200' },
          transition: 'background 0.15s',
        }}
      >
        <SourceIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" fontWeight={typography.button.fontWeight}>
          {sources.length} source{sources.length !== 1 ? 's' : ''}
        </Typography>
        {expanded
          ? <ExpandLessIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          : <ExpandMoreIcon sx={{ fontSize: 13, color: 'text.secondary' }} />}
      </Box>

      {/* Expanded list */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {visible.map((src, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Chip
                size="small"
                label={src.source_type}
                color={sourceTypeColor(src.source_type)}
                variant="filled"
                sx={{ fontSize: typography.overline.fontSize, height: 16, textTransform: 'capitalize', flexShrink: 0 }}
              />
              <Typography
                variant="caption"
                color="text.primary"
                sx={{ lineHeight: typography.caption.lineHeight, wordBreak: 'break-word' }}
              >
                {src.label}
                {!!src.metadata?.study_date && (
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                    · {src.metadata.study_date as string}
                  </Typography>
                )}
              </Typography>
            </Box>
          ))}
          {sources.length > MAX_SOURCES_COLLAPSED && !showAll && (
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ cursor: 'pointer', pl: 1, '&:hover': { textDecoration: 'underline' } }}
              onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
            >
              +{sources.length - MAX_SOURCES_COLLAPSED} more
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Markdown answer renderer ──────────────────────────────────────────────

const AnswerRenderer: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = (key: string) => {
    if (listBuffer.length === 0) return;
    const items = listBuffer.map((item, j) => (
      <Typography
        component="li"
        variant="body2"
        key={j}
        sx={{ lineHeight: typography.body2.lineHeight, mb: 0.25 }}
      >
        {renderInline(item)}
      </Typography>
    ));
    elements.push(
      <Box
        component={listType === 'ol' ? 'ol' : 'ul'}
        key={key}
        sx={{ pl: 2.5, mt: 0.5, mb: 0.75, '& li': { mb: 0.25 } }}
      >
        {items}
      </Box>
    );
    listBuffer = [];
    listType = null;
  };

  lines.forEach((line, i) => {
    const ulMatch = line.match(/^[-*]\s+(.*)/);
    const olMatch = line.match(/^\d+\.\s+(.*)/);

    if (ulMatch) {
      if (listType === 'ol') flushList(`flush-${i}`);
      listType = 'ul';
      listBuffer.push(ulMatch[1]);
    } else if (olMatch) {
      if (listType === 'ul') flushList(`flush-${i}`);
      listType = 'ol';
      listBuffer.push(olMatch[1]);
    } else {
      flushList(`flush-${i}`);
      if (line.trim() === '') {
        elements.push(<Box key={`gap-${i}`} sx={{ mb: 0.75 }} />);
      } else {
        elements.push(
          <Typography key={i} variant="body2" sx={{ lineHeight: typography.body1.lineHeight, mb: 0.25 }}>
            {renderInline(line)}
          </Typography>
        );
      }
    }
  });
  flushList('flush-end');

  return <Box>{elements}</Box>;
};

const TimelineRenderer: React.FC<{ timeline: TimelineEntry[] }> = ({ timeline }) => {
  if (!timeline || timeline.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'primary.light' }}>
      {timeline.map((entry, i) => (
        <Box key={i} sx={{ mb: 1.5, position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              left: -22,
              top: 4,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              border: '2px solid white',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Typography variant="caption" fontWeight="fontWeightMedium" color="primary.main">
              {entry.date || 'Unknown date'}
            </Typography>
            <Chip
              size="small"
              label={entry.modality}
              color="secondary"
              variant="outlined"
              sx={{ height: 16, fontSize: typography.overline.fontSize }}
            />
            <Typography variant="caption" color="text.secondary">
              {entry.study_type}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ lineHeight: typography.body2.lineHeight, color: 'text.primary' }}>
            {entry.findings}
          </Typography>
          {entry.status && (
            <Typography variant="caption" color="text.disabled">
              Status: {entry.status}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

const ProgressionRenderer: React.FC<{ progression: ProgressionEntry }> = ({ progression }) => {
  if (!progression || progression.score === undefined || progression.score === null) return null;

  const direction = progression.direction || 'unknown';
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    resolved: 'success',
    regressed: 'success',
    stable: 'info',
    new: 'warning',
    progressed: 'error',
    mixed: 'warning',
    unknown: 'default',
  };
  const chipColor = colorMap[direction] || 'default';

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={direction.toUpperCase()}
          color={chipColor}
          sx={{ fontWeight: 'fontWeightMedium', fontSize: typography.overline.fontSize }}
        />
        <Typography variant="caption" color="text.secondary">
          Progression score: {progression.score.toFixed(2)}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, lineHeight: typography.body2.lineHeight }}>
        {progression.summary}
      </Typography>

      {progression.current_study && progression.prior_study && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Current</Typography>
            <Typography variant="body2">
              {progression.current_study.modality} {progression.current_study.anatomy_region} ({progression.current_study.date})
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Prior</Typography>
            <Typography variant="body2">
              {progression.prior_study.modality} {progression.prior_study.anatomy_region} ({progression.prior_study.date})
            </Typography>
          </Box>
        </Box>
      )}

      {progression.evidence && progression.evidence.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Evidence
          </Typography>
          {progression.evidence.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Chip
                size="small"
                label={item.status}
                color={item.status === 'progressed' ? 'error' : item.status === 'regressed' ? 'success' : 'info'}
                variant="outlined"
                sx={{ height: 18, fontSize: typography.overline.fontSize }}
              />
              <Typography variant="body2">
                {item.label}
                {item.current_value !== undefined && item.prior_value !== undefined && (
                  <>: {item.prior_value}{item.unit} → {item.current_value}{item.unit} ({item.change_pct}%)</>
                )}
                {item.current_grade && item.prior_grade && (
                  <>: {item.prior_grade} → {item.current_grade}</>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {progression.recommendations && progression.recommendations.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Recommendations
          </Typography>
          {progression.recommendations.map((rec, i) => (
            <Typography key={i} variant="body2" sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
              <span>•</span>
              {rec}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const AddendumRenderer: React.FC<{ content: string; metadata?: Record<string, unknown> }> = ({ content, metadata }) => {
  const status = metadata?.status as string | undefined;
  const addendumId = metadata?.addendum_id as number | undefined;
  const reportId = metadata?.report_id as number | undefined;
  const reason = metadata?.reason as string | undefined;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'success.light',
        borderRadius: 1,
        bgcolor: 'success.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={reason ? reason.toUpperCase() : 'ADDENDUM'}
          color="success"
          sx={{ fontWeight: 'fontWeightMedium', fontSize: typography.overline.fontSize }}
        />
        {status === 'created' && addendumId && (
          <Typography variant="caption" color="text.secondary">
            Addendum #{addendumId}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" sx={{ lineHeight: typography.body2.lineHeight, whiteSpace: 'pre-wrap' }}>
        {content}
      </Typography>
      {reportId && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          Report #{reportId}
        </Typography>
      )}
    </Box>
  );
};

interface CriticalFindingEntry {
  category: string;
  severity: string;
  evidence: string;
  recommendation: string;
  matched_terms?: string[];
}

const CriticalFindingRenderer: React.FC<{
  content: string;
  metadata?: Record<string, unknown>;
}> = ({ content, metadata }) => {
  const findings = (metadata?.findings as CriticalFindingEntry[] | undefined) || [];
  const status = metadata?.status as string | undefined;
  const reportId = metadata?.report_id as number | undefined;

  if (status === 'none') {
    return (
      <Alert severity="success" sx={{ mt: 1.5 }}>
        <Typography variant="body2">{content}</Typography>
        {reportId && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Report #{reportId}
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      {findings.map((finding, index) => (
        <Alert key={index} severity="error" sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={finding.severity.toUpperCase()}
              color="error"
              sx={{ fontWeight: 'fontWeightMedium', fontSize: typography.overline.fontSize }}
            />
            <Typography variant="subtitle2" component="span">
              {finding.category}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Evidence:</strong> {finding.evidence}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Recommendation:</strong> {finding.recommendation}
          </Typography>
          {reportId && (
            <Typography variant="caption" color="text.secondary">
              Report #{reportId}
            </Typography>
          )}
        </Alert>
      ))}
      {!findings.length && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography variant="body2">{content}</Typography>
        </Alert>
      )}
    </Box>
  );
};

interface OperationalQueryRow {
  label: string;
  value: number | string;
  role?: string;
}

const OperationalQueryRenderer: React.FC<{
  content: string;
  metadata?: Record<string, unknown>;
}> = ({ content, metadata }) => {
  const result = (metadata?.result as Record<string, unknown> | undefined) || {};
  const metric = result.metric as string | undefined;
  const groupBy = result.group_by as string | undefined;
  const rows = (result.rows as OperationalQueryRow[] | undefined) || [];
  const valueHours = result.value_hours as number | undefined;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label="OPERATIONAL"
          color="info"
          sx={{ fontWeight: 'fontWeightMedium', fontSize: typography.overline.fontSize }}
        />
        {metric && (
          <Typography variant="caption" color="text.secondary">
            {metric.replace('_', ' ').toUpperCase()}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 1, lineHeight: typography.body2.lineHeight, whiteSpace: 'pre-wrap' }}>
        {content}
      </Typography>

      {metric === 'avg_turnaround' && valueHours !== undefined && (
        <Typography variant="h6" component="div" color="info.main">
          {valueHours} hours
        </Typography>
      )}

      {rows.length > 0 && (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 1 }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ textAlign: 'left', p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                {groupBy ? groupBy.charAt(0).toUpperCase() + groupBy.slice(1) : 'Item'}
              </Box>
              <Box component="th" sx={{ textAlign: 'right', p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                Value
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, index) => (
              <Box component="tr" key={index}>
                <Box component="td" sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  {row.label}
                </Box>
                <Box component="td" sx={{ p: 0.5, textAlign: 'right', borderBottom: '1px solid', borderColor: 'divider' }}>
                  {row.value}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ComparisonRenderer: React.FC<{ comparison: ComparisonEntry }> = ({ comparison }) => {
  if (!comparison || comparison.has_prior === undefined) return null;

  const pct = Math.round((comparison.agreement_score || 0) * 100);
  const agreementColor = pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={comparison.has_prior ? 'COMPARISON' : 'NO PRIOR'}
          color={comparison.has_prior ? 'info' : 'default'}
          sx={{ fontWeight: 'fontWeightMedium', fontSize: typography.overline.fontSize }}
        />
        <Typography variant="caption" color={agreementColor}>
          Agreement: {pct}%
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, lineHeight: typography.body2.lineHeight, whiteSpace: 'pre-line' }}>
        {comparison.summary_text}
      </Typography>

      {comparison.current_study && comparison.prior_study && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Current</Typography>
            <Typography variant="body2">
              {comparison.current_study.modality} {comparison.current_study.anatomy_region} ({comparison.current_study.date})
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Prior</Typography>
            <Typography variant="body2">
              {comparison.prior_study.modality} {comparison.prior_study.anatomy_region} ({comparison.prior_study.date})
            </Typography>
          </Box>
        </Box>
      )}

      {comparison.new_findings && comparison.new_findings.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            New findings
          </Typography>
          {comparison.new_findings.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Chip size="small" label="new" color="warning" variant="outlined" sx={{ height: 18, fontSize: typography.overline.fontSize }} />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {comparison.resolved_findings && comparison.resolved_findings.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Resolved findings
          </Typography>
          {comparison.resolved_findings.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Chip size="small" label="resolved" color="success" variant="outlined" sx={{ height: 18, fontSize: typography.overline.fontSize }} />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {comparison.stable_findings && comparison.stable_findings.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Stable findings
          </Typography>
          {comparison.stable_findings.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Chip size="small" label="stable" color="info" variant="outlined" sx={{ height: 18, fontSize: typography.overline.fontSize }} />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {comparison.measurement_changes && comparison.measurement_changes.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Measurement changes
          </Typography>
          {comparison.measurement_changes.map((item, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>
              {item.label}: {item.prior_value}{item.unit} → {item.current_value}{item.unit} ({item.change_pct}%)
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\[Source\s*\d+\])/g);
  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return (
        <Typography component="span" key={i} variant="body2" fontWeight={typography.h1.fontWeight}>
          {boldMatch[1]}
        </Typography>
      );
    }
    const srcMatch = part.match(/^\[Source\s*(\d+)\]$/);
    if (srcMatch) {
      return (
        <Chip
          key={i}
          label={part}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            fontSize: typography.caption.fontSize,
            height: 17,
            mx: 0.25,
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        />
      );
    }
    return part;
  });
}

// ── Copy-to-clipboard helpers ────────────────────────────────────────────

function formatMessageAsText(msg: ConversationMessage): string {
  let out = msg.role === 'user' ? 'You:\n' : 'AI Copilot:\n';

  const lines = msg.content.split('\n');
  for (const line of lines) {
    const ulMatch = line.match(/^[-*]\s+(.*)/);
    const olMatch = line.match(/^(\d+)\.\s+(.*)/);
    if (ulMatch) {
      out += `  • ${ulMatch[1]}\n`;
    } else if (olMatch) {
      out += `  ${olMatch[1]}. ${olMatch[2]}\n`;
    } else {
      out += `${line.replace(/\*\*([^*]+)\*\*/g, '$1')}\n`;
    }
  }

  if (msg.sources && msg.sources.length > 0) {
    out += '\nSources:\n';
    msg.sources.forEach((s, i) => {
      out += `  [${i + 1}] ${s.source_type.toUpperCase()} — ${s.label}`;
      if (s.metadata?.study_date) out += ` (${s.metadata.study_date as string})`;
      out += '\n';
    });
  }

  return out.trim();
}

// ── PDF export ────────────────────────────────────────────────────────────

async function exportToPDF(messages: ConversationMessage[]): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const html2pdf: any = (await import('html2pdf.js')).default;

  const el = document.createElement('div');
  el.style.fontFamily = 'Georgia, serif';
  el.style.fontSize = '12px';
  el.style.color = '#111';
  el.style.padding = '24px';
  el.style.maxWidth = '720px';

  el.innerHTML = `
    <h2 style="margin:0 0 4px;font-size:16px;color:#3f51b5;">Radiology AI Copilot — Session Export</h2>
    <p style="margin:0 0 20px;font-size:10px;color:#888;">Exported: ${new Date().toLocaleString()}</p>
    <hr style="border:none;border-top:1px solid #ddd;margin-bottom:20px;" />
  `;

  messages.forEach((msg, idx) => {
    const isUser = msg.role === 'user';
    const wrapper = document.createElement('div');
    wrapper.style.marginBottom = '16px';
    wrapper.style.padding = '12px 16px';
    wrapper.style.borderRadius = '6px';
    wrapper.style.backgroundColor = isUser ? '#e8eaf6' : '#ffffff';
    wrapper.style.border = `1px solid ${isUser ? '#c5cae9' : '#e0e0e0'}`;
    wrapper.style.borderLeft = `4px solid ${isUser ? '#3f51b5' : '#7c4dff'}`;
    wrapper.style.pageBreakInside = 'avoid';

    const label = document.createElement('p');
    label.style.margin = '0 0 6px';
    label.style.fontWeight = 'bold';
    label.style.fontSize = '10px';
    label.style.textTransform = 'uppercase';
    label.style.letterSpacing = '0.05em';
    label.style.color = isUser ? '#3f51b5' : '#7c4dff';
    label.textContent = isUser
      ? `You — ${msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : `AI Copilot — ${msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    wrapper.appendChild(label);

    const bodyLines = msg.content.split('\n');
    let listEl: HTMLElement | null = null;
    for (const line of bodyLines) {
      const ulMatch = line.match(/^[-*]\s+(.*)/);
      const olMatch = line.match(/^(\d+)\.\s+(.*)/);
      if (ulMatch || olMatch) {
        if (!listEl) {
          listEl = document.createElement(ulMatch ? 'ul' : 'ol');
          listEl.style.margin = '4px 0';
          listEl.style.paddingLeft = '20px';
          wrapper.appendChild(listEl);
        }
        const li = document.createElement('li');
        li.style.marginBottom = '2px';
        li.textContent = (ulMatch ? ulMatch[1] : (olMatch ? olMatch[2] : '')).replace(/\*\*([^*]+)\*\*/g, '$1');
        listEl.appendChild(li);
      } else {
        listEl = null;
        if (line.trim()) {
          const p = document.createElement('p');
          p.style.margin = '0 0 4px';
          p.style.lineHeight = '1.6';
          p.innerHTML = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
          wrapper.appendChild(p);
        }
      }
    }

    if (msg.sources && msg.sources.length > 0) {
      const srcDiv = document.createElement('div');
      srcDiv.style.marginTop = '8px';
      srcDiv.style.paddingTop = '6px';
      srcDiv.style.borderTop = '1px solid #e0e0e0';
      srcDiv.style.fontSize = '10px';
      srcDiv.style.color = '#555';
      const srcLabel = document.createElement('strong');
      srcLabel.textContent = `Sources (${msg.sources.length}): `;
      srcDiv.appendChild(srcLabel);
      srcDiv.appendChild(document.createTextNode(
        msg.sources.map((s, i) => `[${i + 1}] ${s.label}`).join(' · ')
      ));
      wrapper.appendChild(srcDiv);
    }

    if (idx < messages.length - 1) {
      const sep = document.createElement('div');
      sep.style.marginTop = '4px';
      wrapper.appendChild(sep);
    }

    el.appendChild(wrapper);
  });

  const date = new Date().toISOString().slice(0, 10);
  await html2pdf()
    .from(el)
    .set({
      margin: [12, 12, 12, 12],
      filename: `radiology-copilot-${date}.pdf`,
      image: { type: 'jpeg', quality: 0.97 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .save();
}

// ── Per-message copy button ───────────────────────────────────────────────

const CopyButton: React.FC<{ msg: ConversationMessage }> = ({ msg }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatMessageAsText(msg));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available — silently ignore
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy answer'}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          ml: 'auto',
          color: copied ? 'success.main' : 'text.disabled',
          '&:hover': { color: 'primary.main' },
          transition: 'color 0.2s',
          p: 0.25,
        }}
      >
        {copied
          ? <CheckIcon sx={{ fontSize: 15 }} />
          : <ContentCopyIcon sx={{ fontSize: 15 }} />}
      </IconButton>
    </Tooltip>
  );
};

// ── Session outline sidebar (left) ────────────────────────────────────────

interface SessionOutlineProps {
  messages: ConversationMessage[];
  messageRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onNewConversation: () => void;
}

const SessionOutline: React.FC<SessionOutlineProps> = ({ messages, messageRefs, onNewConversation }) => {
  const userTurns = messages
    .map((m, i) => ({ msg: m, idx: i }))
    .filter(({ msg }) => msg.role === 'user');

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FormatListBulletedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={typography.label.fontWeight} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: typography.overline.letterSpacing, fontSize: typography.caption.fontSize }}>
            Session Outline
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {userTurns.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, py: 1, display: 'block' }}>
            No queries yet
          </Typography>
        )}
        {userTurns.map(({ msg, idx }, turnIdx) => (
          <Box
            key={idx}
            onClick={() => {
              messageRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            sx={{
              px: 1.5,
              py: 0.75,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.75,
              borderLeft: '2px solid transparent',
              '&:hover': {
                bgcolor: 'primary.50',
                borderLeftColor: 'primary.main',
              },
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: typography.overline.fontSize,
                fontWeight: typography.h1.fontWeight,
                color: 'primary.main',
                bgcolor: 'primary.50',
                borderRadius: '50%',
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: '1px',
              }}
            >
              {turnIdx + 1}
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                lineHeight: typography.caption.lineHeight,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: typography.caption.fontSize,
              }}
            >
              {msg.content}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box
          onClick={onNewConversation}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'grey.100', color: 'primary.main' },
            transition: 'background 0.15s',
          }}
        >
          <AddCommentIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" fontWeight={typography.button.fontWeight}>New conversation</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Source hyperlink resolver ─────────────────────────────────────────────

// Session-linked source types — all navigate to /transcribe?session=:sessionId
const SESSION_SOURCE_TYPES = new Set([
  'report', 'transcription', 'study', 'history_index', 'diagnosis_classification',
]);

function sourceHref(src: SourceReference): string | null {
  const t = (src.source_type || '').toLowerCase();
  if (SESSION_SOURCE_TYPES.has(t)) {
    const sessionId = src.metadata?.session_id;
    if (sessionId) return `/transcribe?session=${encodeURIComponent(String(sessionId))}`;
    return null;
  }
  if (t === 'patient_context' || t === 'patient') {
    // Link directly to patient details via query param on the patients page
    const patientId = src.metadata?.patient_id || src.source_id;
    if (patientId) return `/patients?patient_id=${encodeURIComponent(String(patientId))}`;
    return '/patients';
  }
  return null;
}

// Deduplicate sources: for a given turn, collapse all session-linked sources
// that share the same session_id into a single entry. Patient entries are kept.
interface DeduplicatedSource {
  sessionId: string | null;
  label: string;
  types: string[];        // all source_types that map to this session
  study_date: string | null;
  href: string | null;
  src: SourceReference;   // representative source (for metadata)
}

function deduplicateSources(sources: SourceReference[]): DeduplicatedSource[] {
  const sessionMap = new Map<string, DeduplicatedSource>();
  const nonSession: DeduplicatedSource[] = [];

  for (const src of sources) {
    const t = (src.source_type || '').toLowerCase();
    const sessionId = src.metadata?.session_id ? String(src.metadata.session_id) : null;

    if (SESSION_SOURCE_TYPES.has(t) && sessionId) {
      if (sessionMap.has(sessionId)) {
        const existing = sessionMap.get(sessionId);
        if (existing) {
          if (!existing.types.includes(t)) existing.types.push(t);
          if (!existing.study_date && src.metadata?.study_date) {
            existing.study_date = String(src.metadata.study_date);
          }
        }
      } else {
        const study_date = src.metadata?.study_date ? String(src.metadata.study_date) : null;
        const modality = src.metadata?.modality ? String(src.metadata.modality) : null;
        const labelParts: string[] = ['Session'];
        if (study_date) labelParts.push(study_date);
        if (modality) labelParts.push(modality);
        sessionMap.set(sessionId, {
          sessionId,
          label: labelParts.join(' — '),
          types: [t],
          study_date,
          href: `/transcribe?session=${encodeURIComponent(sessionId)}`,
          src,
        });
      }
    } else {
      // Patient, or session-less sources — keep as-is
      nonSession.push({
        sessionId: null,
        label: src.label || src.source_type || '',
        types: [t],
        study_date: src.metadata?.study_date ? String(src.metadata.study_date) : null,
        href: sourceHref(src),
        src,
      });
    }
  }

  // Combine: sessions first (sorted by study_date desc), then non-session
  const sessions = Array.from(sessionMap.values()).sort((a, b) => {
    if (!a.study_date) return 1;
    if (!b.study_date) return -1;
    return b.study_date.localeCompare(a.study_date);
  });
  return [...sessions, ...nonSession];
}

// ── All-sources sidebar (right) ───────────────────────────────────────────
// Sources are grouped PER TURN (per Q→A pair), not globally aggregated.
// Within each turn, session-linked sources (report/study/transcription/
// history_index/diagnosis_classification) that share the same session_id are
// collapsed into a single row with type badges, avoiding duplicate links.

const AllSourcesPanel: React.FC<{ messages: ConversationMessage[] }> = ({ messages }) => {
  const turns: { query: string; sources: SourceReference[] }[] = [];
  messages.forEach((m, i) => {
    if (m.role === 'assistant' && m.sources && m.sources.length > 0) {
      const prev = messages[i - 1];
      turns.push({
        query: prev?.role === 'user' ? prev.content : '',
        sources: m.sources,
      });
    }
  });

  // Track which turns are collapsed (all start expanded)
  const [collapsed, setCollapsed] = React.useState<Record<number, boolean>>({});
  const toggleTurn = (idx: number) =>
    setCollapsed(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Count deduplicated entries for the badge
  const totalDeduped = turns.reduce((acc, t) => acc + deduplicateSources(t.sources).length, 0);

  return (
    <Box
      sx={{
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <SourceIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={typography.label.fontWeight} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: typography.overline.letterSpacing, fontSize: typography.caption.fontSize }}>
            Sources by Turn
          </Typography>
          {totalDeduped > 0 && (
            <Typography variant="caption" sx={{ ml: 'auto', bgcolor: 'primary.main', color: 'white', borderRadius: 99, px: 0.75, py: 0.1, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight }}>
              {totalDeduped}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {turns.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, py: 1.5, display: 'block' }}>
            Sources will appear here as you query
          </Typography>
        )}

        {turns.map((turn, turnIdx) => {
          const deduped = deduplicateSources(turn.sources);
          const isCollapsed = !!collapsed[turnIdx];

          return (
            <Box key={turnIdx} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              {/* Turn header — click to collapse/expand */}
              <Box
                onClick={() => toggleTurn(turnIdx)}
                sx={{
                  px: 1.5, py: 0.75, bgcolor: 'grey.50',
                  display: 'flex', alignItems: 'flex-start', gap: 0.75,
                  cursor: 'pointer', userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight, color: 'primary.main',
                    bgcolor: 'primary.50', borderRadius: '50%', width: 16, height: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, mt: '1px',
                  }}
                >
                  {turnIdx + 1}
                </Typography>
                <Typography
                  variant="caption" color="text.secondary"
                  sx={{ fontSize: typography.caption.fontSize, fontWeight: typography.button.fontWeight, lineHeight: typography.h4.lineHeight, flex: 1,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {turn.query || 'Query'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 0.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: typography.overline.fontSize }}>
                    {deduped.length}
                  </Typography>
                  {isCollapsed
                    ? <ExpandMoreIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    : <ExpandLessIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
                </Box>
              </Box>

              {/* Deduplicated source rows — hidden when collapsed */}
              {!isCollapsed && (
                <>
                  {deduped.map((entry, i) => (
                    <Box
                      key={i}
                      sx={{ px: 1.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25, '&:hover': { bgcolor: 'grey.50' } }}
                    >
                      {/* Type badges for this row */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {entry.types.map(tp => (
                          <Chip
                            key={tp}
                            label={tp.replace(/_/g, ' ')}
                            size="small"
                            color={sourceTypeColor(tp)}
                            variant="filled"
                            sx={{ fontSize: typography.overline.fontSize, height: 13, textTransform: 'capitalize' }}
                          />
                        ))}
                      </Box>
                      {/* Link or plain label */}
                      {entry.href ? (
                        <Typography
                          component="a"
                          href={entry.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{
                            lineHeight: typography.caption.lineHeight, display: 'block', fontSize: typography.caption.fontSize,
                            color: 'primary.main', textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          title={entry.label}
                        >
                          ↗ {entry.label}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption" color="text.primary"
                          sx={{ lineHeight: typography.caption.lineHeight, display: 'block', fontSize: typography.caption.fontSize,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={entry.label}
                        >
                          {entry.label}
                        </Typography>
                      )}
                      {entry.study_date && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                          {entry.study_date}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ── Chat content (shared between float panel and full-screen dialog) ──────

interface ChatContentProps {
  messages: ConversationMessage[];
  loading: boolean;
  error: string | null;
  input: string;
  onInputChange: (v: string) => void;
  onSubmit: (q?: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFollowUp: (s: string) => void;
  onClearError: () => void;
  onVoiceAddendum?: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messageRefs?: React.MutableRefObject<(HTMLDivElement | null)[]>;
  messagesAreaHeight: string | number;
}

const ChatContent: React.FC<ChatContentProps> = ({
  messages,
  loading,
  error,
  input,
  onInputChange,
  onSubmit,
  onKeyDown,
  onFollowUp,
  onClearError,
  onVoiceAddendum,
  messagesEndRef,
  messageRefs,
  messagesAreaHeight,
}) => (
  <>
    {/* Messages area */}
    <Box
      sx={{
        px: 1.5,
        pt: 1.5,
        pb: 0.5,
        height: messagesAreaHeight,
        overflowY: 'auto',
        bgcolor: 'grey.50',
      }}
    >
      {messages.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 6, px: 3 }}>
          <AutoAwesomeIcon sx={{ fontSize: 44, color: 'grey.300', mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={typography.button.fontWeight}>
            Ask me about patients, studies, reports, or diagnoses.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Try: &quot;Summarize this patient&apos;s imaging history&quot; or
            &quot;Addendum: small nodule unchanged from prior&quot;
          </Typography>
        </Box>
      )}

      {messages.map((msg, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 2,
          }}
        >
          <Box
            ref={messageRefs ? (el: HTMLDivElement | null) => { messageRefs.current[i] = el; } : undefined}
            sx={{ maxWidth: '90%', display: 'flex', flexDirection: 'column' }}
          >
            <Card
              variant="outlined"
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                borderColor: msg.role === 'user' ? 'primary.100' : 'grey.200',
                borderLeftWidth: 3,
                bgcolor: msg.role === 'user' ? 'primary.50' : 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                {/* Role row */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.5, '&:hover .copy-btn': { opacity: 1 } }}>
                  <Typography
                    variant="caption"
                    fontWeight={typography.label.fontWeight}
                    color={msg.role === 'user' ? 'primary.main' : 'secondary.main'}
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: typography.caption.fontSize }}
                  >
                    {msg.role === 'user' ? 'You' : 'AI Copilot'}
                  </Typography>
                  {msg.confidence !== undefined && msg.role === 'assistant' && (
                    <ConfidenceIndicator confidence={msg.confidence} />
                  )}
                  {msg.intent && (
                    <Chip
                      size="small"
                      label={msg.intent.replace(/_/g, ' ')}
                      sx={{ height: 16, fontSize: typography.overline.fontSize, ml: 0.25 }}
                      variant="outlined"
                      color="default"
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: typography.overline.fontSize, whiteSpace: 'nowrap' }}
                  >
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {msg.role === 'assistant' && (
                    <Box sx={{ ml: 'auto' }}>
                      <CopyButton msg={msg} />
                    </Box>
                  )}
                </Box>

                {/* Answer content */}
                {msg.role === 'assistant'
                  ? <AnswerRenderer content={msg.content} />
                  : (
                    <Typography variant="body2" sx={{ lineHeight: typography.body2.lineHeight }}>
                      {msg.content}
                    </Typography>
                  )
                }

                {/* Patient timeline visualization */}
                {msg.intent === 'patient_timeline' && msg.metadata && (
                  <TimelineRenderer
                    timeline={(msg.metadata.timeline as TimelineEntry[]) || []}
                  />
                )}

                {/* Disease progression visualization */}
                {msg.intent === 'progression_analysis' && msg.metadata && (
                  <ProgressionRenderer
                    progression={(msg.metadata.progression as ProgressionEntry) || undefined}
                  />
                )}

                {/* Report comparison visualization */}
                {msg.intent === 'study_comparison' && msg.metadata && (
                  <ComparisonRenderer
                    comparison={(msg.metadata.comparison as ComparisonEntry) || undefined}
                  />
                )}

                {/* Addendum visualization */}
                {msg.intent === 'addendum_request' && (
                  <AddendumRenderer
                    content={msg.content}
                    metadata={msg.metadata}
                  />
                )}

                {/* Critical finding visualization */}
                {msg.intent === 'critical_finding' && (
                  <CriticalFindingRenderer
                    content={msg.content}
                    metadata={msg.metadata}
                  />
                )}

                {/* Operational query visualization */}
                {msg.intent === 'operational_query' && (
                  <OperationalQueryRenderer
                    content={msg.content}
                    metadata={msg.metadata}
                  />
                )}

                {/* Sources */}
                {msg.sources && msg.sources.length > 0 && (
                  <SourcesList sources={msg.sources} />
                )}
              </CardContent>
            </Card>

            {/* Follow-up suggestions — outside card, full width */}
            {msg.followUpSuggestions && msg.followUpSuggestions.length > 0 && (
              <Box sx={{ mt: 0.75, px: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mb: 0.5, fontSize: typography.caption.fontSize }}
                >
                  Suggested follow-ups
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {msg.followUpSuggestions.map((s, j) => (
                    <Chip
                      key={j}
                      label={s}
                      size="small"
                      variant="outlined"
                      color="primary"
                      clickable
                      onClick={() => onFollowUp(s)}
                      sx={{
                        fontSize: typography.caption.fontSize,
                        height: 26,
                        borderRadius: '13px',
                        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ))}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Analyzing…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      <div ref={messagesEndRef} />
    </Box>

    {loading && <LinearProgress sx={{ height: 2 }} />}

    {/* Input bar */}
    <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about patients, studies, reports..."
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={loading}
          multiline
          maxRows={4}
          sx={{
            '& .MuiInputBase-root': { fontSize: typography.body2.fontSize, borderRadius: 2 },
          }}
        />
        <Tooltip title="Dictate addendum">
          <span>
            <IconButton
              color="secondary"
              onClick={onVoiceAddendum}
              disabled={loading || !onVoiceAddendum}
              size="small"
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
              }}
            >
              <MicIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          color="primary"
          onClick={() => onSubmit()}
          disabled={!input.trim() || loading}
          size="small"
          sx={{
            bgcolor: input.trim() && !loading ? 'primary.main' : 'grey.200',
            color: input.trim() && !loading ? 'white' : 'grey.400',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.200', color: 'grey.400' },
            width: 36,
            height: 36,
            flexShrink: 0,
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  </>
);

// ── Shared header ─────────────────────────────────────────────────────────

interface CopilotHeaderProps {
  conversationId: string | null;
  isInline: boolean;
  isMaximized: boolean;
  onNewConversation: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  onClose: () => void;
  onExportPDF?: () => void;
  pdfDisabled?: boolean;
  borderRadius?: string;
}

const CopilotHeader: React.FC<CopilotHeaderProps> = ({
  conversationId,
  isInline,
  isMaximized,
  onNewConversation,
  onMaximize,
  onMinimize,
  onClose,
  onExportPDF,
  pdfDisabled,
  borderRadius,
}) => (
  <Box
    sx={{
      px: 1.5,
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      borderRadius: borderRadius ?? 0,
      userSelect: 'none',
      flexShrink: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AutoAwesomeIcon sx={{ fontSize: 18 }} />
      <Typography variant="subtitle2" fontWeight={typography.h1.fontWeight} sx={{ letterSpacing: typography.body2.letterSpacing }}>
        Radiology AI Copilot
      </Typography>
      {conversationId && (
        <Chip
          size="small"
          icon={<HistoryIcon sx={{ fontSize: 11 }} aria-hidden="true" />}
          label="Active"
          sx={{
            height: 17,
            fontSize: '0.62rem',
            bgcolor: 'rgba(255,255,255,0.18)',
            color: 'inherit',
            '& .MuiChip-icon': { color: 'inherit' },
          }}
        />
      )}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {onExportPDF && (
        <Tooltip title="Export session as PDF">
          <span>
            <IconButton size="small" onClick={onExportPDF} disabled={pdfDisabled} sx={{ color: pdfDisabled ? 'rgba(255,255,255,0.35)' : 'inherit' }}>
              <PictureAsPdfIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Open in Intelligence Workspace">
        <IconButton size="small" component="a" href="/intelligence" sx={{ color: 'inherit' }}>
          <OpenInNewIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="New conversation">
        <IconButton size="small" onClick={onNewConversation} sx={{ color: 'inherit' }}>
          <AddCommentIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      {!isInline && (
        isMaximized ? (
          <Tooltip title="Shrink">
            <IconButton size="small" onClick={onMinimize} sx={{ color: 'inherit' }}>
              <CloseFullscreenIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Maximize">
            <IconButton size="small" onClick={onMaximize} sx={{ color: 'inherit' }}>
              <OpenInFullIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )
      )}
      {!isInline && !isMaximized && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

// ── Main Widget Component ────────────────────────────────────────────────

const RadiologyCopilotWidget: React.FC<RadiologyCopilotWidgetProps> = ({
  patientId,
  studyId,
  sessionId,
  variant = 'fab',
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [isMaximized, setIsMaximized] = useState(false);
  const [panelSize, setPanelSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const theme = useTheme();
  const showSidebars = useMediaQuery(theme.breakpoints.up('lg'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resizeStartRef = useRef<{ x: number; y: number; w: number; h: number; dir: string } | null>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Build context from props
  const buildContext = useCallback((): AIQueryContext => {
    const ctx: AIQueryContext = {};
    if (patientId) ctx.patient_id = patientId;
    if (studyId) ctx.study_id = studyId;
    if (sessionId) ctx.session_id = sessionId;
    return ctx;
  }, [patientId, studyId, sessionId]);

  // Submit query
  const handleSubmit = useCallback(async (queryText?: string) => {
    const query = (queryText || input).trim();
    if (!query || loading) return;

    setInput('');
    setError(null);

    const userMsg: ConversationMessage = {
      role: 'user',
      content: query,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const response: AIQueryResponse = await aiCopilotService.submitQuery({
        query,
        context: buildContext(),
        conversation_id: conversationId || undefined,
      });

      if (response.metadata?.conversation_id) {
        setConversationId(response.metadata.conversation_id as string);
      }

      const assistantMsg: ConversationMessage = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources,
        confidence: response.confidence,
        intent: response.intent,
        followUpSuggestions: response.follow_up_suggestions,
        metadata: response.metadata,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to get response';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [input, loading, buildContext, conversationId]);

  // Handle Enter key
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleFollowUp = useCallback((suggestion: string) => {
    handleSubmit(suggestion);
  }, [handleSubmit]);

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, []);

  // ── Drag-resize logic ─────────────────────────────────────────────────

  const startResize = useCallback((e: React.MouseEvent, dir: string) => {
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, y: e.clientY, w: panelSize.w, h: panelSize.h, dir };

    const onMove = (ev: MouseEvent) => {
      if (!resizeStartRef.current) return;
      const { x, y, w, h, dir: d } = resizeStartRef.current;
      const dx = x - ev.clientX;
      const dy = y - ev.clientY;
      setPanelSize({
        w: d.includes('left') ? Math.max(MIN_W, w + dx) : w,
        h: d.includes('top') ? Math.max(MIN_H, h + dy) : h,
      });
    };

    const onUp = () => {
      resizeStartRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelSize]);

  // ── Shared chat props ─────────────────────────────────────────────────

  const handleVoiceAddendum = useCallback(() => {
    setInput(prev => {
      if (prev.trim()) return prev;
      return 'Addendum: ';
    });
  }, []);

  const chatProps = {
    messages,
    loading,
    error,
    input,
    onInputChange: setInput,
    onSubmit: handleSubmit,
    onKeyDown: handleKeyDown,
    onFollowUp: handleFollowUp,
    onClearError: () => setError(null),
    onVoiceAddendum: handleVoiceAddendum,
    messagesEndRef,
    messageRefs,
  };

  const handleExportPDF = useCallback(() => {
    if (messages.length > 0) exportToPDF(messages);
  }, [messages]);

  // ── Render FAB ────────────────────────────────────────────────────────

  const renderFab = () => (
    <Fab
      color="primary"
      size="medium"
      onClick={() => setOpen(!open)}
      sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
    >
      {open ? <CloseIcon /> : (
        <Badge badgeContent={messages.length > 0 ? messages.length : undefined} color="error" max={99}>
          <SmartToyIcon />
        </Badge>
      )}
    </Fab>
  );

  // ── Render floating panel ─────────────────────────────────────────────

  const renderPanel = () => {
    const isInline = variant === 'inline';
    const HEADER_H = 50;
    const INPUT_H = 76;
    const msgAreaH = isInline
      ? 'calc(100% - 130px)'
      : panelSize.h - HEADER_H - INPUT_H;

    const panelSx = isInline
      ? { width: '100%', height: '100%', display: 'flex', flexDirection: 'column' as const }
      : {
          position: 'fixed' as const,
          bottom: 88,
          right: 24,
          width: panelSize.w,
          height: panelSize.h,
          zIndex: 1100,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden',
        };

    return (
      <Paper elevation={isInline ? 1 : 0} sx={panelSx}>
        {/* Resize handles — left edge, top edge, top-left corner */}
        {!isInline && (
          <>
            <Box
              onMouseDown={(e) => startResize(e, 'left')}
              sx={{
                position: 'absolute', left: 0, top: 8, bottom: 8, width: 5,
                cursor: 'ew-resize', zIndex: 10,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.25 },
                borderRadius: '4px 0 0 4px',
              }}
            />
            <Box
              onMouseDown={(e) => startResize(e, 'top')}
              sx={{
                position: 'absolute', top: 0, left: 8, right: 8, height: 5,
                cursor: 'ns-resize', zIndex: 10,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.25 },
              }}
            />
            <Box
              onMouseDown={(e) => startResize(e, 'top-left')}
              sx={{
                position: 'absolute', top: 0, left: 0, width: 10, height: 10,
                cursor: 'nwse-resize', zIndex: 11,
              }}
            />
          </>
        )}

        <CopilotHeader
          conversationId={conversationId}
          isInline={isInline}
          isMaximized={false}
          onNewConversation={handleNewConversation}
          onMaximize={() => setIsMaximized(true)}
          onMinimize={() => setIsMaximized(false)}
          onClose={() => setOpen(false)}
          onExportPDF={handleExportPDF}
          pdfDisabled={messages.length === 0}
          borderRadius={isInline ? '4px 4px 0 0' : '8px 8px 0 0'}
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ChatContent {...chatProps} messagesAreaHeight={msgAreaH} />
        </Box>
      </Paper>
    );
  };

  // ── Render maximized full-screen dialog ────────────────────────────────

  const renderMaximized = () => (
    <Dialog
      open={isMaximized}
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.50',
          overflow: 'hidden',
        },
      }}
    >
      <CopilotHeader
        conversationId={conversationId}
        isInline={false}
        isMaximized={true}
        onNewConversation={handleNewConversation}
        onMaximize={handleNewConversation /* never called in maximized state */}
        onMinimize={() => setIsMaximized(false)}
        onClose={() => { setIsMaximized(false); setOpen(false); }}
        onExportPDF={handleExportPDF}
        pdfDisabled={messages.length === 0}
        borderRadius="0"
      />
      {/* Three-column layout: left sidebar | chat | right sidebar */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {showSidebars && (
          <SessionOutline
            messages={messages}
            messageRefs={messageRefs}
            onNewConversation={handleNewConversation}
          />
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ChatContent {...chatProps} messagesAreaHeight="calc(100vh - 130px)" />
        </Box>
        {showSidebars && (
          <AllSourcesPanel messages={messages} />
        )}
      </Box>
    </Dialog>
  );

  // ── Main render ───────────────────────────────────────────────────────

  if (variant === 'inline') {
    return renderPanel();
  }

  return (
    <>
      {renderFab()}
      {open && renderPanel()}
      {renderMaximized()}
    </>
  );
};

export default RadiologyCopilotWidget;
